import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { FilterOption } from '@/types/filters.types';
import { getIntegrationOrderFilterOptions } from '@/services/integrations.service';

/**
 * Filter options for Marketplace orders, by canonical key — the Axiom and
 * Partell pages (one vendor, `integrationKey`) and the Contact List page
 * (every vendor). One API builder serves both (IntegrationOrderFilterOptions).
 * `statusLabels` names fulfillment statuses the way the page's table does.
 * `ensureStatuses` are statuses a KPI tile can select: offered even when no
 * order has them yet, so a tile never sets a Status the rail cannot show.
 */
export function useIntegrationOrderFilterOptions(opts?: {
  integrationKey?: string;
  statusLabels?: Record<string, string>;
  ensureStatuses?: readonly string[];
  enabled?: boolean;
}): Record<string, FilterOption[]> {
  const { integrationKey, statusLabels, ensureStatuses, enabled = true } = opts ?? {};
  const { data } = useQuery({
    queryKey: ['integration-order-filter-options', integrationKey ?? 'all'],
    queryFn: () => getIntegrationOrderFilterOptions(integrationKey),
    staleTime: 60_000,
    enabled,
  });

  return useMemo<Record<string, FilterOption[]>>(() => {
    const out: Record<string, FilterOption[]> = {};
    for (const [key, options] of Object.entries(data ?? {})) {
      out[key] = options.map((o) => ({
        value: String(o.value),
        label: key === 'order-status' ? statusLabels?.[o.value] ?? o.label : o.label,
      }));
    }
    if (data && ensureStatuses?.length) {
      const statuses = out['order-status'] ?? [];
      const missing = ensureStatuses
        .filter((s, i) => ensureStatuses.indexOf(s) === i && !statuses.some((o) => o.value === s))
        .map((s) => ({ value: s, label: statusLabels?.[s] ?? headline(s) }));
      if (missing.length) {
        out['order-status'] = [...statuses, ...missing].sort((a, b) => a.label.localeCompare(b.label));
      }
    }
    return out;
  }, [data, statusLabels, ensureStatuses]);
}

/** in_production → "In Production", as the API labels a status (Str::headline). */
function headline(status: string): string {
  return status.split('_').filter(Boolean).map((w) => w[0].toUpperCase() + w.slice(1)).join(' ');
}
